(function (global) {
  if (global.BodycareAssets) return;

  const scriptPromises = new Map();
  const stylePromises = new Map();

  function absoluteUrl(url) {
    return new URL(url, document.baseURI).href;
  }

  function existingElement(selector, url, attribute) {
    const target = absoluteUrl(url);
    return Array.from(document.querySelectorAll(selector)).find((element) => {
      const value = element.getAttribute(attribute);
      return value && absoluteUrl(value) === target;
    });
  }

  function loadScript(url, attributes = {}) {
    const key = absoluteUrl(url);
    if (scriptPromises.has(key)) return scriptPromises.get(key);

    const promise = new Promise((resolve, reject) => {
      const existing = existingElement('script[src]', key, 'src');
      if (existing) {
        if (existing.dataset.bodycareLoading === 'true') {
          existing.addEventListener('load', () => resolve(existing), { once: true });
          existing.addEventListener('error', reject, { once: true });
        } else {
          resolve(existing);
        }
        return;
      }

      const script = document.createElement('script');
      script.src = key;
      script.dataset.bodycareLoading = 'true';
      Object.entries(attributes).forEach(([name, value]) => {
        if (value === true) script.setAttribute(name, '');
        else if (value !== false && value != null) script.setAttribute(name, String(value));
      });
      script.addEventListener('load', () => {
        delete script.dataset.bodycareLoading;
        resolve(script);
      }, { once: true });
      script.addEventListener('error', (error) => {
        scriptPromises.delete(key);
        reject(error);
      }, { once: true });
      document.head.appendChild(script);
    });

    scriptPromises.set(key, promise);
    return promise;
  }

  function loadStyle(url, attributes = {}) {
    const key = absoluteUrl(url);
    if (stylePromises.has(key)) return stylePromises.get(key);

    const promise = new Promise((resolve, reject) => {
      const existing = existingElement('link[href]', key, 'href');
      if (existing) {
        resolve(existing);
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = key;
      Object.entries(attributes).forEach(([name, value]) => {
        if (value !== false && value != null) link.setAttribute(name, String(value));
      });
      link.addEventListener('load', () => resolve(link), { once: true });
      link.addEventListener('error', (error) => {
        stylePromises.delete(key);
        reject(error);
      }, { once: true });
      document.head.appendChild(link);
    });

    stylePromises.set(key, promise);
    return promise;
  }

  function load(resources = []) {
    return Promise.all(resources.map((resource) => (
      resource.type === 'css'
        ? loadStyle(resource.path, resource.attributes)
        : loadScript(resource.path, resource.attributes)
    )));
  }

  function init(root = document) {
    const manifests = root.matches?.('[data-bodycare-assets]')
      ? [root]
      : root.querySelectorAll?.('[data-bodycare-assets]') || [];

    manifests.forEach((manifest) => {
      if (manifest.dataset.bodycareAssetsReady === 'true') return;
      manifest.dataset.bodycareAssetsReady = 'true';
      try {
        load(JSON.parse(manifest.textContent)).catch((error) => {
          console.warn('Unable to load component assets.', error);
        });
      } catch (error) {
        console.warn('Invalid component asset manifest.', error);
      }
    });

    const intentManifests = root.matches?.('[data-bodycare-intent-assets]')
      ? [root]
      : root.querySelectorAll?.('[data-bodycare-intent-assets]') || [];

    intentManifests.forEach((manifest) => {
      if (manifest.dataset.bodycareIntentReady === 'true') return;
      manifest.dataset.bodycareIntentReady = 'true';
      const scope = manifest.closest('.shopify-section') || document;
      const selector = manifest.dataset.intentSelector || 'input, button, a';
      const controller = new AbortController();

      const handleIntent = (event) => {
        if (!event.target.closest?.(selector)) return;
        controller.abort();

        let resources;
        try {
          resources = JSON.parse(manifest.textContent);
        } catch (error) {
          console.warn('Invalid intent asset manifest.', error);
          return;
        }

        load(resources).then(() => {
          if (event.type === 'input' && event.target.value) {
            event.target.dispatchEvent(new Event('input', { bubbles: true }));
          } else if (event.type === 'focusin' && event.target.value) {
            event.target.dispatchEvent(new Event('focus', { bubbles: false }));
          }
        }).catch((error) => console.warn('Unable to load intent assets.', error));
      };

      ['pointerover', 'focusin', 'touchstart', 'input'].forEach((eventName) => {
        scope.addEventListener(eventName, handleIntent, { capture: true, passive: eventName !== 'input', signal: controller.signal });
      });
    });
  }

  global.BodycareAssets = { loadScript, loadStyle, load, init, scriptPromises, stylePromises };
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('bodycare:content:loaded', (event) => init(event.target));
})(window);
